import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Phase 9B — SideDrawer.
 *
 * A context-preserving drawer used for the roster shift editor and any
 * flow where the user benefits from keeping the surrounding page
 * visible. Below `md` it collapses to a bottom-sheet.
 *
 * Behaviour:
 *   * Focus trap by default (auto-focuses the first focusable
 *     element on open).
 *   * Escape closes.
 *   * Backdrop click closes only when `dirty === false` — dirty
 *     drawers must confirm through their own footer.
 *   * `footer` slot renders sticky at the bottom (Save · Cancel etc.).
 *
 * This wraps a hand-rolled overlay rather than shadcn `sheet.jsx`
 * because we need the persistent-across-actions behaviour (drawer
 * stays open while the user creates the next shift), which shadcn's
 * dialog-based sheet doesn't model cleanly.
 */
export default function SideDrawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  dirty = false,
  width = "md",
  testid = "side-drawer",
}) {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKey);
    // Focus the first focusable in the drawer.
    const t = setTimeout(() => {
      const first = rootRef.current?.querySelector(
        "input,select,textarea,button,[tabindex]:not([tabindex='-1'])"
      );
      first?.focus();
    }, 40);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", handleKey);
      try { previous?.focus?.(); } catch { /* ignore */ }
    };
  }, [open, onClose]);

  const widths = {
    sm: "md:max-w-[380px]",
    md: "md:max-w-[480px]",
    lg: "md:max-w-[640px]",
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid={`${testid}-backdrop`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => { if (!dirty) onClose?.(); }}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
          />
          <motion.aside
            ref={rootRef}
            data-testid={testid}
            role="dialog"
            aria-modal="true"
            aria-label={title || "Panel"}
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
            className={`fixed z-50 bg-[hsl(var(--surface))] border hairline shadow-[var(--shadow-lg,0_20px_60px_-15px_rgba(0,0,0,0.3))] flex flex-col
              inset-x-0 bottom-0 max-h-[92vh] rounded-t-2xl md:rounded-2xl
              md:inset-0 md:m-auto md:h-fit md:max-h-[86vh] md:w-full ${widths[width] || widths.md}`}
          >
            <header className="flex items-start justify-between gap-3 px-5 py-4 border-b hairline shrink-0">
              <div className="min-w-0">
                <h2
                  className="font-display text-[17px] tracking-tight text-[hsl(var(--text-strong))] truncate"
                  data-testid={`${testid}-title`}
                >
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-[13px] text-[hsl(var(--text-muted))] mt-0.5">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                data-testid={`${testid}-close`}
                aria-label="Close"
                className="h-9 w-9 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-strong))] transition-colors focus-visible:ring-2 focus-visible:ring-[hsl(var(--focus-ring))] outline-none"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </header>
            <div
              className="flex-1 overflow-y-auto px-5 py-4"
              data-testid={`${testid}-body`}
            >
              {children}
            </div>
            {footer && (
              <footer
                className="px-5 py-3 border-t hairline shrink-0 bg-[hsl(var(--surface))] flex items-center justify-end gap-2"
                data-testid={`${testid}-footer`}
              >
                {footer}
              </footer>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
